import { useState, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft, Edit2, DollarSign, AlertCircle, AlertTriangle, RefreshCw, X, Loader2, Package, ClipboardList, Activity, Receipt, RotateCcw, PackageCheck, Globe, FileSpreadsheet, Stethoscope, Box, History, ChevronRight, CalendarX2, Tags,
} from 'lucide-react'
import { format, parseISO } from 'date-fns'
import { getAdminClients, getAdminOrders, getOrderCarriers, getOrderCarrierServices, getOrderStates, getOrderTags, getClientPricing, updatePricing, updateOrderInsertType, updateOrderCasePick, updateOrderFlatRateException, exportOrders, getMtdCounts, applyInsertDefaultToAllOrders, waiveOrder, downloadClientWorkbook, getInsertPeriods, saveInsertPeriods, getOrderTrackingEventsFull, getProductsAdminAccess } from '../../lib/api'
import { EditClientModal } from './Clients'
import StatsCard from '../../components/StatsCard'
import OrderTable from '../../components/OrderTable'
import EditOrderFeesModal from '../../components/EditOrderFeesModal'
import FilterBar from '../../components/FilterBar'
import StorageTab from '../../components/StorageTab'
import WarehouseTab from '../../components/WarehouseTab'
import ExportsTab from '../../components/ExportsTab'
import OrderDetailsTab from '../../components/OrderDetailsTab'
import PackagingTab from '../../components/PackagingTab'
import DetailReportTab from '../../components/DetailReportTab'
import SelfShipTab from '../../components/SelfShipTab'
import ReturnsTab from '../../components/ReturnsTab'
import PostageDiscrepanciesTab from '../../components/PostageDiscrepanciesTab'
import RolloverTab from '../../components/RolloverTab'
import ProductsTab from '../../components/ProductsTab'
import { ToastContainer, useToast } from '../../components/Toast'

function formatCurrency(v) {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
  }).format(v ?? 0)
}

const EMPTY_TIER = { min_orders: '', max_orders: '', fee: '' }

// Default pick fee tiers (ascending by min_orders)
const DEFAULT_PICK_TIERS = [
  { min_orders: 0,    max_orders: 500,  fee: 1.8  },
  { min_orders: 501,  max_orders: 1000, fee: 1.45 },
  { min_orders: 1001, max_orders: 5000, fee: 1.3  },
  { min_orders: 5001, max_orders: '',   fee: 1.1  },
]

// Default additional item fee tiers
const DEFAULT_ITEM_TIERS = [
  { min_orders: 0,    max_orders: 500,  fee: 0.8 },
  { min_orders: 501,  max_orders: 1000, fee: 0.7 },
  { min_orders: 1001, max_orders: 5000, fee: 0.6 },
  { min_orders: 5001, max_orders: '',   fee: 0.4 },
]

const DEFAULT_TIERS = Array.from({ length: 4 }, () => ({ ...EMPTY_TIER }))

// Parse insert_trigger_sku field (single SKU, comma/newline-separated, or array)
// into an array of trimmed unique SKUs.
function parseTriggerSkus(raw) {
  if (!raw) return []
  const list = Array.isArray(raw)
    ? raw
    : String(raw).split(/[,;\n\r\t]+/)
  const seen = new Set()
  const out = []
  for (const s of list) {
    const t = (s ?? '').trim()
    if (t && !seen.has(t)) { seen.add(t); out.push(t) }
  }
  return out
}

function buildTiers(raw, defaults = null, count = 4) {
  // If no saved tiers exist, use the supplied defaults
  if ((!raw || raw.length === 0) && defaults) return defaults.map(t => ({ ...t }))
  const tiers = (raw || []).map(t => ({
    min_orders: t.min_orders ?? '',
    max_orders: t.max_orders ?? '',
    fee: t.fee ?? '',
  }))
  while (tiers.length < count) tiers.push({ ...EMPTY_TIER })
  return tiers
}

function TierTable({ tiers, onChange, feeLabel = 'Fee ($)' }) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-2 text-xs font-semibold text-slate-500 uppercase tracking-wider px-1">
        <span>Min Orders</span>
        <span>Max Orders</span>
        <span>{feeLabel}</span>
      </div>
      {tiers.map((tier, i) => (
        <div key={i} className="grid grid-cols-3 gap-2">
          <input type="number" min="0" step="1" value={tier.min_orders}
            onChange={e => onChange(i, 'min_orders', e.target.value)}
            className="input text-sm" placeholder="0" />
          <input type="number" min="0" step="1" value={tier.max_orders}
            onChange={e => onChange(i, 'max_orders', e.target.value)}
            className="input text-sm" placeholder="No limit" />
          <input type="number" min="0" step="0.01" value={tier.fee}
            onChange={e => onChange(i, 'fee', e.target.value)}
            className="input text-sm" placeholder="0.00" />
        </div>
      ))}
    </div>
  )
}

function PricingModal({ client, carriers = [], onClose, onSuccess }) {
  const pricingToForm = (p) => ({
    default_no_insert:    p?.default_no_insert ?? false,
    insert_trigger_skus:  parseTriggerSkus(p?.insert_trigger_sku),
    insert_trigger_input: '',
    insert_multiplier_skus:  parseTriggerSkus(p?.insert_multiplier_skus),
    insert_multiplier_input: '',
    insert_multiplier:       p?.insert_multiplier ?? '',
    pick_tiers:       buildTiers(p?.pick_fee_tiers,            DEFAULT_PICK_TIERS),
    item_tiers:       buildTiers(p?.additional_item_fee_tiers, DEFAULT_ITEM_TIERS),
    labelling_fee:    p?.labelling_fee    ?? 0.55,
    insert_fee:       p?.insert_fee       ?? 0.1,
    custom_insert_fee:p?.custom_insert_fee ?? 0.55,
    case_pick_fee:    p?.case_pick_fee    ?? 0,
    carrier_margins:      { default: p?.carrier_margins?.default      ?? '', ...(p?.carrier_margins      || {}) },
    own_account_carriers: p?.own_account_carriers || [],
    carrier_flat_margins: { default: p?.carrier_flat_margins?.default ?? '', ...(p?.carrier_flat_margins || {}) },
    flat_outbound_rate:           p?.flat_outbound_rate           ?? '',
    flat_outbound_rates:          p?.flat_outbound_rates          || {},
    kit_component_prefixes:       (p?.kit_component_prefixes || []).join(', '),
    order_aliquoting_fee:         p?.order_aliquoting_fee ?? 0,
    order_aliquoting_exclude_skus: p?.order_aliquoting_exclude_skus || '',
    flat_return_rate:             p?.flat_return_rate             ?? '',
    shipping_exception_threshold: p?.shipping_exception_threshold ?? '',
    tag_fees: (p?.tag_fees ?? []).map(tf => ({ tag: tf.tag ?? '', fee: tf.fee ?? '', overrides_pick: tf.overrides_pick ?? false })),
  })

  const [form, setForm] = useState(() => pricingToForm(client.pricing))
  const { toasts, addToast, removeToast } = useToast()
  const queryClient = useQueryClient()

  // Effective-dated insert periods (separate table; saved alongside pricing).
  const { data: insertPeriodsData, error: insertPeriodsError } = useQuery({
    queryKey: ['admin', 'insert-periods', client.id],
    queryFn: () => getInsertPeriods(client.id),
  })
  const [periods, setPeriods] = useState(null)   // null until loaded from server
  if (insertPeriodsData && periods === null) {
    setPeriods(insertPeriodsData.map(p => ({ ...p, _key: crypto.randomUUID() })))
  }
  const workingPeriods = periods ?? []

  // Services this client has actually shipped on, for the per-carrier margin
  // accordion. Purely a picker — a carrier with no services still edits fine,
  // so a failed/empty fetch degrades to the old carrier-only list.
  // isError is deliberately surfaced: this query failing used to render as
  // "no services shipped", which reads as a fact about the data rather than a
  // broken request — it hid a bad column name for a whole release.
  const {
    data: carrierServices = [],
    isError: servicesFailed,
  } = useQuery({
    queryKey: ['admin', 'order-services', client.id],
    queryFn: () => getOrderCarrierServices(client.id),
    staleTime: 5 * 60 * 1000,
  })
  const [openCarriers, setOpenCarriers] = useState(() => new Set())

  function toggleCarrier(carrier) {
    setOpenCarriers(prev => {
      const next = new Set(prev)
      next.has(carrier) ? next.delete(carrier) : next.add(carrier)
      return next
    })
  }

  // Which carrier is showing its "add service" picker, if any.
  const [addingFor, setAddingFor] = useState(null)

  // Adds a service the client has actually shipped on. The code comes from the
  // dropdown rather than free text so the margin key is guaranteed to match
  // orders.service at billing time — a typo'd code silently prices nothing.
  function addService(carrier, code) {
    if (!code) return
    const key = `${carrier}:${code}`
    setForm(prev => ({
      ...prev,
      carrier_margins:      { ...prev.carrier_margins,      [key]: '' },
      carrier_flat_margins: { ...prev.carrier_flat_margins, [key]: '' },
    }))
    setAddingFor(null)
  }

  // Clearing a service margin DELETES its keys rather than storing 0 — a
  // stored 0 is a real "charge no margin on this service" instruction, while
  // an absent key means "inherit the carrier". They must stay distinguishable.
  function clearService(key) {
    setForm(prev => {
      const margins     = { ...prev.carrier_margins }
      const flatMargins = { ...prev.carrier_flat_margins }
      delete margins[key]
      delete flatMargins[key]
      return { ...prev, carrier_margins: margins, carrier_flat_margins: flatMargins }
    })
  }
  const addPeriod    = () => setPeriods(prev => [...(prev ?? []), { _key: crypto.randomUUID(), mode: 'standard', start_date: '', end_date: '' }])
  const removePeriod = (key) => setPeriods(prev => (prev ?? []).filter(p => p._key !== key))
  const updatePeriod = (key, field, value) => setPeriods(prev => (prev ?? []).map(p => p._key === key ? { ...p, [field]: value } : p))

  useEffect(() => {
    if (!carriers.length) return
    setForm(prev => {
      const margins      = { ...prev.carrier_margins }
      const flatMargins  = { ...prev.carrier_flat_margins }
      carriers.forEach(c => {
        if (!(c in margins))     margins[c]     = ''
        if (!(c in flatMargins)) flatMargins[c] = ''
      })
      return { ...prev, carrier_margins: margins, carrier_flat_margins: flatMargins }
    })
  }, [carriers])

  const mutation = useMutation({
    mutationFn: async ({ pricing, insertPeriods }) => {
      await updatePricing(client.id, pricing)
      await saveInsertPeriods(client.id, insertPeriods)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'clients'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'pricing', client.id] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'orders', client.id] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'insert-periods', client.id] })
      onSuccess()
    },
    onError: (err) => {
      addToast({ type: 'error', message: err.response?.data?.detail || 'Failed to update pricing.' })
    },
  })

  function handlePickTier(i, field, value) {
    setForm(prev => {
      const pick_tiers = [...prev.pick_tiers]
      pick_tiers[i] = { ...pick_tiers[i], [field]: value }
      return { ...prev, pick_tiers }
    })
  }

  function handleItemTier(i, field, value) {
    setForm(prev => {
      const item_tiers = [...prev.item_tiers]
      item_tiers[i] = { ...item_tiers[i], [field]: value }
      return { ...prev, item_tiers }
    })
  }

  function handleMarginChange(carrier, value) {
    setForm(prev => ({ ...prev, carrier_margins: { ...prev.carrier_margins, [carrier]: value } }))
  }

  function handleFlatMarginChange(carrier, value) {
    setForm(prev => ({ ...prev, carrier_flat_margins: { ...prev.carrier_flat_margins, [carrier]: value } }))
  }

  function addCarrier() {
    const name = window.prompt('Carrier name (e.g. auspost, dhl, tnt):')
    if (!name?.trim()) return
    setForm(prev => ({
      ...prev,
      carrier_margins:      { ...prev.carrier_margins,      [name.trim().toLowerCase()]: '' },
      carrier_flat_margins: { ...prev.carrier_flat_margins, [name.trim().toLowerCase()]: '' },
    }))
  }

  function removeCarrier(carrier) {
    // Drop the carrier AND every service override under it — leaving orphaned
    // "carrier:service" keys behind would keep pricing that carrier through a
    // row the admin can no longer see.
    const owned = key => key === carrier || key.startsWith(`${carrier}:`)
    setForm(prev => {
      const margins     = { ...prev.carrier_margins }
      const flatMargins = { ...prev.carrier_flat_margins }
      Object.keys(margins).filter(owned).forEach(k => delete margins[k])
      Object.keys(flatMargins).filter(owned).forEach(k => delete flatMargins[k])
      return { ...prev, carrier_margins: margins, carrier_flat_margins: flatMargins }
    })
  }

  function parseTiers(tiers) {
    return tiers
      .filter(t => t.fee !== '' && t.fee !== null)
      .map(t => ({
        min_orders: t.min_orders !== '' ? Number(t.min_orders) : 0,
        max_orders: t.max_orders !== '' ? Number(t.max_orders) : null,
        fee: Number(t.fee),
      }))
  }

  // Margin keys are either a carrier ("australia_post") or one service within a
  // carrier ("australia_post:3D55"). The top level of the accordion lists only
  // carriers; service keys are rendered inside their carrier's fold.
  //
  // A key can be priced by percent, by flat $, or both, and handleSubmit drops
  // '' values — so a service given ONLY a $ margin has no key in
  // carrier_margins once it has round-tripped through the API. Deriving these
  // lists from carrier_margins alone made that service disappear from the
  // editor on reload while its $ margin stayed live in billing
  // (pricing.py::_get_carrier_flat_margin resolves per service). Union the two
  // maps everywhere, as the backend already does in rate_estimator.py.
  // flat_outbound_rates belongs in this union for the SAME reason: a service
  // priced with a flat rate and no margin has no key in either margin map once
  // it has round-tripped through the API, so it vanished from the editor while
  // billing kept charging its flat rate. That is the normal configuration for a
  // flat-rate client, not an edge case.
  const marginKeys = Array.from(new Set([
    ...Object.keys(form.carrier_margins),
    ...Object.keys(form.carrier_flat_margins),
    ...Object.keys(form.flat_outbound_rates || {}),
  ]))

  const serviceKeysFor = carrier => marginKeys.filter(k => k.startsWith(`${carrier}:`))

  const carriers_in_form = Array.from(new Set(
    marginKeys.map(k => k.split(':')[0])
  ))

  const normKey = s => String(s || '').trim().toLowerCase().replace(/[\s-]+/g, '_')

  // Everything this client has shipped on under a carrier, from their own order
  // history. This is the authoritative option list: orders.service is exactly
  // what a margin key must match, so offering anything else invites a key that
  // prices nothing.
  function shippedServicesFor(carrier) {
    return carrierServices
      .filter(s => normKey(s.carrier) === normKey(carrier))
      .map(s => ({ code: s.service_code, name: s.service_type, count: s.order_count }))
  }

  // Services with a margin of their own. Priced-but-unshipped keys must survive
  // here — history is windowed, and a service that drops out of the window must
  // not silently lose its margin.
  function configuredServicesFor(carrier) {
    const shipped = new Map(shippedServicesFor(carrier).map(s => [normKey(s.code), s]))
    return serviceKeysFor(carrier)
      .map(k => {
        const code = k.slice(carrier.length + 1)
        const hit  = shipped.get(normKey(code))
        return { code, name: hit?.name ?? null, count: hit?.count ?? null }
      })
      .sort((a, b) => (b.count ?? 0) - (a.count ?? 0) || a.code.localeCompare(b.code))
  }

  // Dropdown options: shipped, minus whatever is already priced.
  function availableServicesFor(carrier) {
    const taken = new Set(
      serviceKeysFor(carrier).map(k => normKey(k.slice(carrier.length + 1)))
    )
    return shippedServicesFor(carrier)
      .filter(s => !taken.has(normKey(s.code)))
      .sort((a, b) => (b.count ?? 0) - (a.count ?? 0) || a.code.localeCompare(b.code))
  }

  // Carriers the client ships on their OWN account: we still charge fulfilment,
  // but the carrier invoices them directly, so shipping bills at $0.
  const isOwnAccount = carrier =>
    (form.own_account_carriers || []).some(c => normKey(c) === normKey(carrier))

  // Own account is the HIGHEST-priority rule in the whole calculator — it
  // returns $0 before any flat-rate or margin logic runs. It was only ever
  // shown as a checkbox at the end of a margin row, visually subordinate to
  // the two inputs it silently annihilates.
  const ownAccountCarriers = (form.own_account_carriers || []).filter(Boolean)

  function toggleOwnAccount(carrier) {
    setForm(prev => {
      const cur = prev.own_account_carriers || []
      const on  = cur.some(c => normKey(c) === normKey(carrier))
      return {
        ...prev,
        own_account_carriers: on
          ? cur.filter(c => normKey(c) !== normKey(carrier))
          : [...cur, carrier],
      }
    })
  }

  // A flat rate for ONE service. Set it and that service bills this amount
  // instead of cost + margin; leave it blank and the service falls through to
  // the margin columns, which is how international and courier services bill.
  function handleServiceFlatRate(key, value) {
    setForm(prev => ({
      ...prev,
      flat_outbound_rates: { ...(prev.flat_outbound_rates || {}), [key]: value },
    }))
  }

  // Per-service flat rates supersede the single client-wide rate, so several
  // parts of this form change meaning once any one of them is set.
  const perServiceRateCount = Object.values(form.flat_outbound_rates || {})
    .filter(v => v !== '' && v !== null && Number(v) > 0).length
  // The backend's test is plain dict truthiness — ANY key supersedes the
  // client-wide scalar, even one holding 0. Mirror that exactly, or the UI
  // shows a field as live that the calculator has already stopped reading.
  const anyPerServiceRateKey = Object.keys(form.flat_outbound_rates || {}).length > 0
  // Is ANY flat rate in force? Decides whether the threshold and the kit rule
  // have anything to act on — both are inert without one.
  const anyFlatOutboundConfigured =
    anyPerServiceRateKey || Number(form.flat_outbound_rate) > 0

  const serviceOverrideCount = carrier => serviceKeysFor(carrier).length

  // True when this exact key carries a margin of its own. Blank counts as absent
  // because handleSubmit drops blank fields — they are never saved as 0, so a
  // blank carrier row means unnamed services fall through to Default, then to 0.
  const hasOwnMargin = key => {
    const pct  = form.carrier_margins[key]
    const flat = form.carrier_flat_margins[key]
    return (pct !== undefined && pct !== '') || (flat !== undefined && flat !== '')
  }

  function handleSubmit(e) {
    e.preventDefault()
    const carrier_margins = {}
    Object.entries(form.carrier_margins).forEach(([k, v]) => {
      if (v !== '') carrier_margins[k] = Number(v)
    })
    const carrier_flat_margins = {}
    Object.entries(form.carrier_flat_margins).forEach(([k, v]) => {
      if (v !== '') carrier_flat_margins[k] = Number(v)
    })
    mutation.mutate({
      pricing: {
        pick_fee_tiers: parseTiers(form.pick_tiers),
        additional_item_fee_tiers: parseTiers(form.item_tiers),
        labelling_fee: form.labelling_fee !== '' ? Number(form.labelling_fee) : 0,
        insert_fee: form.insert_fee !== '' ? Number(form.insert_fee) : 0,
        custom_insert_fee: form.custom_insert_fee !== '' ? Number(form.custom_insert_fee) : 0,
        case_pick_fee: form.case_pick_fee !== '' ? Number(form.case_pick_fee) : 0,
        carrier_margins,
        carrier_flat_margins,
        own_account_carriers: form.own_account_carriers,
        flat_outbound_rate:           form.flat_outbound_rate !== '' ? Number(form.flat_outbound_rate) : 0,
        // Blank entries are dropped server-side: a 0 key would bill that
        // service free, rather than falling through to cost + margin.
        flat_outbound_rates: Object.fromEntries(
          Object.entries(form.flat_outbound_rates || {})
            .filter(([, v]) => v !== '' && v !== null && Number(v) > 0)
            .map(([k, v]) => [k, Number(v)])
        ),
        order_aliquoting_fee: Number(form.order_aliquoting_fee) || 0,
        order_aliquoting_exclude_skus: String(form.order_aliquoting_exclude_skus || '').trim(),
        kit_component_prefixes: String(form.kit_component_prefixes || '')
          .split(',').map(v => v.trim()).filter(Boolean),
        flat_return_rate:             form.flat_return_rate !== '' ? Number(form.flat_return_rate) : 0,
        shipping_exception_threshold: form.shipping_exception_threshold !== '' ? Number(form.shipping_exception_threshold) : 0,
        default_no_insert:  form.default_no_insert,
        // Merge any unsubmitted typed input plus the current chip list, then
        // send as a comma-separated string (backend parses to a set).
        insert_trigger_sku: (() => {
          const merged = [...form.insert_trigger_skus, ...parseTriggerSkus(form.insert_trigger_input)]
          const unique = Array.from(new Set(merged.filter(Boolean)))
          return unique.length ? unique.join(', ') : null
        })(),
        // SKUs that take more than one insert (e.g. jewellery). Same format.
        insert_multiplier_skus: (() => {
          const merged = [...form.insert_multiplier_skus, ...parseTriggerSkus(form.insert_multiplier_input)]
          const unique = Array.from(new Set(merged.filter(Boolean)))
          return unique.length ? unique.join(', ') : null
        })(),
        insert_multiplier: form.insert_multiplier !== '' && Number(form.insert_multiplier) > 1
          ? Math.floor(Number(form.insert_multiplier))
          : null,
        tag_fees: form.tag_fees
          .filter(tf => tf.tag.trim() !== '')
          .map(tf => ({ tag: tf.tag.trim(), fee: tf.fee !== '' ? Number(tf.fee) : 0, overrides_pick: !!tf.overrides_pick })),
      },
      insertPeriods: workingPeriods
        .filter(p => ['standard', 'none', 'custom'].includes(p.mode))
        .map(p => ({ mode: p.mode, start_date: p.start_date || null, end_date: p.end_date || null })),
    })
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50">
      <div className="flex min-h-full items-start justify-center p-4 py-8">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl">
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-200">
          <div>
            <h3 className="font-semibold text-slate-800">Edit Pricing</h3>
            <p className="text-xs text-slate-400 mt-0.5">{client.name}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="px-6 py-5 space-y-6">

            {/* Pick Fee Tiers */}
            <div>
              <h4 className="text-sm font-semibold text-slate-700 mb-1">Pick Fee Tiers</h4>
              <p className="text-xs text-slate-400 mb-3">Per-order fee based on monthly order volume. Leave max blank for unlimited.</p>
              <TierTable tiers={form.pick_tiers} onChange={handlePickTier} feeLabel="Fee per Order ($)" />
            </div>

            {/* Additional Item Fee Tiers */}
            <div>
              <h4 className="text-sm font-semibold text-slate-700 mb-1">Additional Item Fee Tiers</h4>
              <p className="text-xs text-slate-400 mb-3">Per-item fee for each item after the first, based on monthly order volume.</p>
              <TierTable tiers={form.item_tiers} onChange={handleItemTier} feeLabel="Fee per Item ($)" />
            </div>

            {/* Flat per-order fees */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { key: 'labelling_fee',    label: 'Labelling',     hint: 'Flat fee per order' },
                { key: 'insert_fee',       label: 'Insert',        hint: 'Standard insert per order' },
                { key: 'custom_insert_fee',label: 'Custom Insert', hint: 'When marked custom insert' },
                { key: 'case_pick_fee',    label: 'Case Pick',     hint: 'Replaces pick & item fees' },
              ].map(({ key, label, hint }) => (
                <div key={key}>
                  <label className="label truncate">{label} ($)</label>
                  <input type="number" min="0" step="0.01"
                    value={form[key]}
                    onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
                    className="input" placeholder="0.00" />
                  <p className="text-xs text-slate-400 mt-1 leading-tight">{hint}</p>
                </div>
              ))}
            </div>

            {/* Default Insert Behaviour */}
            <label className="flex items-start gap-3 cursor-pointer rounded-xl border border-slate-200 bg-slate-50 p-4">
              <input
                type="checkbox"
                checked={form.default_no_insert}
                onChange={e => setForm(p => ({ ...p, default_no_insert: e.target.checked }))}
                className="mt-0.5 w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-400"
              />
              <div>
                <p className="text-sm font-medium text-slate-700">No Insert by Default</p>
                <p className="text-xs text-slate-400 mt-0.5">
                  New synced orders will have insert fee = $0. The Insert and Custom Insert rates above are preserved for manual overrides per order. The ShipStation "Custom Insert" tag still overrides this.
                </p>
              </div>
            </label>

            {/* Insert Periods (effective-dated standard / none / custom) */}
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm font-medium text-slate-700 mb-1">Insert Periods (scheduled)</p>
              <p className="text-xs text-slate-400 mb-3">
                Schedule when inserts are standard, off, or custom for date ranges — overrides the default above for orders dated in each window (per-order overrides &amp; trigger SKUs still win). Leave dates blank for "always". After saving, Reprice the client to push changes into the dashboard / MTD.
              </p>
              {/* A failed fetch leaves `periods` null, so the list below renders
                  empty — indistinguishable from "nothing scheduled", and saving
                  would then write that empty list over the real periods. */}
              {insertPeriodsError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 mb-3 flex items-start gap-2">
                  <AlertTriangle size={14} className="text-red-500 shrink-0 mt-0.5" />
                  <div className="text-xs text-red-700">
                    <b>Couldn't load insert periods.</b> This is <b>not</b> an empty schedule —
                    any existing periods are hidden, and saving now would overwrite them.
                    <span className="font-mono block mt-0.5 break-all">
                      {insertPeriodsError?.response?.data?.detail || insertPeriodsError?.message || 'Unknown error'}
                    </span>
                  </div>
                </div>
              )}
              {workingPeriods.length > 0 && (
                <div className="grid grid-cols-[1.3fr_1fr_1fr_auto] gap-2 px-1 mb-1">
                  <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Mode</span>
                  <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">From</span>
                  <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">To</span>
                  <span />
                </div>
              )}
              {workingPeriods.map(row => (
                <div key={row._key} className="grid grid-cols-[1.3fr_1fr_1fr_auto] gap-2 items-center mb-2">
                  <select value={row.mode} onChange={e => updatePeriod(row._key, 'mode', e.target.value)} className="input text-sm">
                    <option value="standard">Standard inserts</option>
                    <option value="none">No inserts</option>
                    <option value="custom">Custom inserts</option>
                  </select>
                  <input type="date" value={row.start_date || ''} onChange={e => updatePeriod(row._key, 'start_date', e.target.value)} className="input text-sm" title="Blank = from the beginning" />
                  <input type="date" value={row.end_date || ''} onChange={e => updatePeriod(row._key, 'end_date', e.target.value)} className="input text-sm" title="Blank = open-ended / current" />
                  <button type="button" onClick={() => removePeriod(row._key)} className="p-1.5 text-slate-400 hover:text-red-500 rounded">
                    <X size={14} />
                  </button>
                </div>
              ))}
              <button type="button" onClick={addPeriod} className="btn-secondary text-xs py-1.5 px-3 mt-1">
                + Add period
              </button>
            </div>

            {/* Insert Trigger SKUs */}
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm font-medium text-slate-700 mb-1">Insert Trigger SKUs</p>
              <p className="text-xs text-slate-400 mb-2">
                If set, the standard insert fee is charged once per order whenever any of these SKUs is present — regardless of "No Insert by Default". Add multiple SKUs to trigger the same flat insert fee. Leave empty to use the default insert behaviour.
                Supports wildcards: <span className="font-mono">SS-*</span> matches every SKU starting with “SS-”.
              </p>
              <div className="flex flex-wrap items-center gap-1.5 p-2 bg-white border border-slate-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-400 focus-within:border-blue-400">
                {form.insert_trigger_skus.map((sku, i) => (
                  <span
                    key={`${sku}-${i}`}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 text-xs font-mono"
                  >
                    {sku}
                    <button
                      type="button"
                      onClick={() => setForm(p => ({
                        ...p,
                        insert_trigger_skus: p.insert_trigger_skus.filter((_, j) => j !== i),
                      }))}
                      className="text-amber-400 hover:text-amber-600"
                      aria-label={`Remove ${sku}`}
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  value={form.insert_trigger_input}
                  onChange={e => {
                    const v = e.target.value
                    // If user typed a separator, commit any complete SKUs immediately
                    if (/[,;\n]/.test(v)) {
                      const parts = parseTriggerSkus(v)
                      setForm(p => ({
                        ...p,
                        insert_trigger_skus: Array.from(new Set([...p.insert_trigger_skus, ...parts])),
                        insert_trigger_input: '',
                      }))
                    } else {
                      setForm(p => ({ ...p, insert_trigger_input: v }))
                    }
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === 'Tab') {
                      const trimmed = form.insert_trigger_input.trim()
                      if (trimmed) {
                        e.preventDefault()
                        setForm(p => ({
                          ...p,
                          insert_trigger_skus: Array.from(new Set([...p.insert_trigger_skus, trimmed])),
                          insert_trigger_input: '',
                        }))
                      }
                    } else if (e.key === 'Backspace' && !form.insert_trigger_input) {
                      // Backspace on empty input removes last chip
                      setForm(p => ({
                        ...p,
                        insert_trigger_skus: p.insert_trigger_skus.slice(0, -1),
                      }))
                    }
                  }}
                  onBlur={() => {
                    const trimmed = form.insert_trigger_input.trim()
                    if (trimmed) {
                      setForm(p => ({
                        ...p,
                        insert_trigger_skus: Array.from(new Set([...p.insert_trigger_skus, trimmed])),
                        insert_trigger_input: '',
                      }))
                    }
                  }}
                  className="flex-1 min-w-[140px] outline-none border-0 p-0 text-sm font-mono bg-transparent"
                  placeholder={form.insert_trigger_skus.length ? 'Add another…' : 'e.g. FLYER-RED, SAMPLE-PACK'}
                />
              </div>
              <p className="text-xs text-slate-400 mt-1.5">
                Press Enter, Tab, or comma to add. Click the × to remove.
              </p>
            </div>

            {/* Multi-insert SKUs */}
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-start justify-between gap-4 mb-1">
                <div>
                  <p className="text-sm font-medium text-slate-700">Multi-insert SKUs</p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    Orders containing any of these SKUs get more than one insert — they bill the standard
                    insert fee × the count below (e.g. 2 × $0.10 = $0.20). Every other order keeps its single
                    standard insert. Leave empty for one insert on every order.
                    Supports wildcards: <span className="font-mono">SS-*</span> matches every SKU starting with
                    “SS-”. Entries without a <span className="font-mono">*</span> match exactly.
                  </p>
                </div>
                <label className="shrink-0 text-xs text-slate-500">
                  Inserts per order
                  <input
                    type="number"
                    min="2"
                    step="1"
                    value={form.insert_multiplier}
                    onChange={e => setForm(p => ({ ...p, insert_multiplier: e.target.value }))}
                    placeholder="2"
                    className="input text-sm w-20 mt-1"
                  />
                </label>
              </div>
              <div className="flex flex-wrap items-center gap-1.5 p-2 bg-white border border-slate-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-400 focus-within:border-blue-400">
                {form.insert_multiplier_skus.map((sku, i) => (
                  <span
                    key={`${sku}-${i}`}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-200 text-xs font-mono"
                  >
                    {sku}
                    <button
                      type="button"
                      onClick={() => setForm(p => ({
                        ...p,
                        insert_multiplier_skus: p.insert_multiplier_skus.filter((_, j) => j !== i),
                      }))}
                      className="text-violet-400 hover:text-violet-600"
                      aria-label={`Remove ${sku}`}
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  value={form.insert_multiplier_input}
                  onChange={e => {
                    const v = e.target.value
                    if (/[,;\n]/.test(v)) {
                      const parts = parseTriggerSkus(v)
                      setForm(p => ({
                        ...p,
                        insert_multiplier_skus: Array.from(new Set([...p.insert_multiplier_skus, ...parts])),
                        insert_multiplier_input: '',
                      }))
                    } else {
                      setForm(p => ({ ...p, insert_multiplier_input: v }))
                    }
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === 'Tab') {
                      const trimmed = form.insert_multiplier_input.trim()
                      if (trimmed) {
                        e.preventDefault()
                        setForm(p => ({
                          ...p,
                          insert_multiplier_skus: Array.from(new Set([...p.insert_multiplier_skus, trimmed])),
                          insert_multiplier_input: '',
                        }))
                      }
                    } else if (e.key === 'Backspace' && !form.insert_multiplier_input) {
                      setForm(p => ({
                        ...p,
                        insert_multiplier_skus: p.insert_multiplier_skus.slice(0, -1),
                      }))
                    }
                  }}
                  onBlur={() => {
                    const trimmed = form.insert_multiplier_input.trim()
                    if (trimmed) {
                      setForm(p => ({
                        ...p,
                        insert_multiplier_skus: Array.from(new Set([...p.insert_multiplier_skus, trimmed])),
                        insert_multiplier_input: '',
                      }))
                    }
                  }}
                  className="flex-1 min-w-[140px] outline-none border-0 p-0 text-sm font-mono bg-transparent"
                  placeholder={form.insert_multiplier_skus.length ? 'Add another…' : 'e.g. SS-* or JEW-RING'}
                />
              </div>
              <p className="text-xs text-slate-400 mt-1.5">
                Press Enter, Tab, or comma to add. Click the × to remove.
              </p>
            </div>

            {/* Tag Fees */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <h4 className="text-sm font-semibold text-slate-700">Tag Fees</h4>
                <button type="button"
                  onClick={() => setForm(p => ({ ...p, tag_fees: [...p.tag_fees, { tag: '', fee: '' }] }))}
                  className="text-xs text-blue-600 hover:text-blue-700">
                  + Add tag fee
                </button>
              </div>
              <p className="text-xs text-slate-400 mb-3">
                Flat fee applied per order when the matching ShipStation tag is present. Tag names are case-sensitive.
              </p>
              {form.tag_fees.length === 0 ? (
                <p className="text-xs text-slate-400 italic">No tag fees configured.</p>
              ) : (
                <div className="space-y-2">
                  <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-2 items-center px-1">
                    <span className="text-xs text-slate-400">Tag name</span>
                    <span className="text-xs text-slate-400 text-right w-28">Fee</span>
                    <span className="text-xs text-slate-400 text-center w-24">Replaces pick fee</span>
                    <span />
                  </div>
                  {form.tag_fees.map((tf, i) => (
                    <div key={i} className="grid grid-cols-[1fr_auto_auto_auto] gap-x-2 items-center">
                      <input
                        type="text"
                        value={tf.tag}
                        onChange={e => setForm(p => {
                          const tag_fees = [...p.tag_fees]
                          tag_fees[i] = { ...tag_fees[i], tag: e.target.value }
                          return { ...p, tag_fees }
                        })}
                        className="input text-sm"
                        placeholder="Tag name (e.g. Fragile)"
                      />
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                        <input
                          type="number" min="0" step="0.01"
                          value={tf.fee}
                          onChange={e => setForm(p => {
                            const tag_fees = [...p.tag_fees]
                            tag_fees[i] = { ...tag_fees[i], fee: e.target.value }
                            return { ...p, tag_fees }
                          })}
                          className="input pl-6 text-sm w-28"
                          placeholder="0.00"
                        />
                      </div>
                      <div className="flex justify-center w-24">
                        <input
                          type="checkbox"
                          checked={!!tf.overrides_pick}
                          onChange={e => setForm(p => {
                            const tag_fees = [...p.tag_fees]
                            tag_fees[i] = { ...tag_fees[i], overrides_pick: e.target.checked }
                            return { ...p, tag_fees }
                          })}
                          className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-400"
                          title="When checked, pick fee and additional item fee are replaced by this tag fee"
                        />
                      </div>
                      <button type="button"
                        onClick={() => setForm(p => ({ ...p, tag_fees: p.tag_fees.filter((_, j) => j !== i) }))}
                        className="text-slate-300 hover:text-red-400">
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Carrier Margins */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <h4 className="text-sm font-semibold text-slate-700">Shipping Margins</h4>
                <button type="button" onClick={addCarrier} className="text-xs text-blue-600 hover:text-blue-700">
                  + Add carrier
                </button>
              </div>
              {/* The old caption ended "Only applies when no flat rate is set",
                  which was wrong twice over: margins price every service the
                  per-service map does not name, AND they are what an escaped
                  order bills at. It also denied flat rates in the very section
                  where they are now entered. */}
              <p className="text-xs text-slate-400 mb-3">
                The price for one service lives on one row. A row bills <b>cost + margin</b>
                unless its flat-rate box is filled, in which case the flat rate wins and the
                margin columns become the fallback used only when an escape fires.
                Blank inherits the carrier row above it; a carrier with no row of its own
                inherits "default". Blank and 0 are different prices — blank inherits,
                0 means no markup and bills at cost.
              </p>
              {ownAccountCarriers.length > 0 && (
                <p className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 mb-3">
                  <b>{ownAccountCarriers.join(', ')}</b> {ownAccountCarriers.length === 1 ? 'is' : 'are'} own-account:
                  every order on {ownAccountCarriers.length === 1 ? 'it' : 'them'} bills <b>$0</b> shipping.
                  Margins and flat rates below are kept but not used — untick to make them live again.
                </p>
              )}
              <div className="space-y-1.5">
                {carriers_in_form.map(carrier => {
                  const isDefault = carrier === 'default'
                  const services  = isDefault ? [] : configuredServicesFor(carrier)
                  const available = isDefault ? [] : availableServicesFor(carrier)
                  const isOpen    = openCarriers.has(carrier)
                  const overrides = serviceOverrideCount(carrier)
                  const ownAcct   = !isDefault && isOwnAccount(carrier)
                  return (
                    <div
                      key={carrier}
                      className={`rounded-lg border transition-colors ${
                        isOpen ? 'border-slate-300 bg-slate-50/60' : 'border-transparent'
                      }`}
                    >
                      {/* Carrier row — the margin every service inherits */}
                      <div className="flex items-center gap-2 px-1.5 py-1">
                        {isDefault ? (
                          <span className="w-5 shrink-0" />
                        ) : (
                          <button
                            type="button"
                            onClick={() => toggleCarrier(carrier)}
                            className="w-5 shrink-0 text-slate-400 hover:text-slate-600"
                            aria-expanded={isOpen}
                            aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${carrier} services`}
                          >
                            <ChevronRight
                              size={14}
                              className={`transition-transform ${isOpen ? 'rotate-90' : ''}`}
                            />
                          </button>
                        )}
                        <span className={`text-sm w-32 shrink-0 font-medium truncate ${isDefault ? 'text-slate-500 italic' : 'text-slate-700'}`}>
                          {isDefault ? 'Default' : carrier}
                        </span>
                        <input
                          type="number" min="0" step="0.1"
                          value={form.carrier_margins[carrier] ?? ''}
                          onChange={e => handleMarginChange(carrier, e.target.value)}
                          className="input w-24 text-sm disabled:bg-slate-100 disabled:text-slate-400"
                          placeholder="0.0"
                          disabled={ownAcct}
                        />
                        <span className="text-sm text-slate-400">%</span>
                        <span className="text-slate-300 text-sm">+</span>
                        <span className="text-sm text-slate-400">$</span>
                        <input
                          type="number" min="0" step="0.01"
                          value={form.carrier_flat_margins[carrier] ?? ''}
                          onChange={e => handleFlatMarginChange(carrier, e.target.value)}
                          className="input w-24 text-sm disabled:bg-slate-100 disabled:text-slate-400"
                          placeholder="0.00"
                          disabled={ownAcct}
                        />
                        {/* Client holds the carrier account — the carrier bills
                            them directly, so we charge $0 freight. Margins are
                            disabled because there is no cost of ours to mark up. */}
                        {!isDefault && (
                          <label
                            className="flex items-center gap-1.5 text-xs cursor-pointer shrink-0 select-none"
                            title={`Client is invoiced by ${carrier} directly — shipping bills at $0`}
                          >
                            <input
                              type="checkbox"
                              checked={ownAcct}
                              onChange={() => toggleOwnAccount(carrier)}
                              className="rounded border-slate-300"
                            />
                            <span className={ownAcct ? 'text-emerald-700 font-medium' : 'text-slate-500'}>
                              Own account
                            </span>
                          </label>
                        )}
                        {overrides > 0 && !isOpen && (
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700">
                            {overrides} service{overrides === 1 ? '' : 's'}
                          </span>
                        )}
                        {!isDefault && (
                          <button type="button" onClick={() => removeCarrier(carrier)} className="text-slate-300 hover:text-red-400 ml-auto">
                            <X size={14} />
                          </button>
                        )}
                      </div>

                      {/* Per-service margins */}
                      {isOpen && (
                        <div className="pl-8 pr-2 pb-2 space-y-1">
                          {/* A blank field is dropped on save, not stored as 0. So
                              pricing ONLY named services, with the carrier row and
                              Default left blank, ships every other service at cost. */}
                          {services.length > 0 && !hasOwnMargin(carrier) && !hasOwnMargin('default') && (
                            <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mb-1">
                              Only the services below have a margin. Anything else on
                              this carrier ships at <b>cost</b> — set a margin on the
                              carrier row above to put a floor under it.
                            </p>
                          )}
                          {services.length === 0 && (
                            <p className="text-xs text-slate-400 py-1">
                              No per-service margins. Every service on this carrier is
                              charged the carrier rate above.
                            </p>
                          )}
                          {services.map(svc => {
                            const key = `${carrier}:${svc.code}`
                            const pct  = form.carrier_margins[key]
                            const flat = form.carrier_flat_margins[key]
                            const svcRate    = (form.flat_outbound_rates || {})[key]
                            const svcRateSet = svcRate !== undefined && svcRate !== '' && Number(svcRate) > 0
                            const isSet = (pct !== undefined && pct !== '') ||
                                          (flat !== undefined && flat !== '')
                            // Own account short-circuits to $0 BEFORE the
                            // flat-rate branch is reached, so every control on
                            // this row is dead — not just the carrier row's.
                            const rowDead = ownAcct
                            // The margins are NOT superseded by a flat rate.
                            // They are what an excepted order bills at, so they
                            // stay editable; an earlier version disabled them
                            // here and that hid the only way to price the
                            // exception.
                            const marginHint = ownAcct
                              ? `Not used — ${carrier} bills $0 on the client's own account`
                              : svcRateSet
                                ? 'Used when the flat rate is waived — a per-order exception, or carrier cost above the threshold.'
                                : undefined
                            // Flat rate set, and no margin resolvable at this
                            // service, its carrier, or Default: an excepted
                            // order then bills at bare COST, no markup.
                            const exceptionBillsAtCost =
                              svcRateSet && !isSet &&
                              !hasOwnMargin(carrier) && !hasOwnMargin('default')
                            return (
                              <div key={key} className="flex items-center gap-2">
                                <div className="w-32 shrink-0 min-w-0">
                                  <p className={`text-xs font-medium truncate ${isSet ? 'text-slate-700' : 'text-slate-500'}`}>
                                    {svc.code}
                                  </p>
                                  {svc.name && (
                                    <p className="text-[10px] text-slate-400 truncate" title={svc.name}>
                                      {svc.name}
                                    </p>
                                  )}
                                </div>
                                {/* Placeholder shows the inherited carrier value, so a
                                    blank field visibly means "same as carrier". */}
                                <input
                                  type="number" min="0" step="0.1"
                                  value={pct ?? ''}
                                  onChange={e => handleMarginChange(key, e.target.value)}
                                  className="input w-20 text-sm disabled:bg-slate-100 disabled:text-slate-400"
                                  placeholder={String(form.carrier_margins[carrier] ?? 0)}
                                  disabled={rowDead}
                                  title={marginHint}
                                />
                                <span className="text-sm text-slate-400">%</span>
                                <span className="text-slate-300 text-sm">+</span>
                                <span className="text-sm text-slate-400">$</span>
                                <input
                                  type="number" min="0" step="0.01"
                                  value={flat ?? ''}
                                  onChange={e => handleFlatMarginChange(key, e.target.value)}
                                  className="input w-20 text-sm disabled:bg-slate-100 disabled:text-slate-400"
                                  placeholder={Number(form.carrier_flat_margins[carrier] ?? 0).toFixed(2)}
                                  disabled={rowDead}
                                  title={marginHint}
                                />
                                {/* Flat rate for this one service. Set = bill
                                    exactly this; blank = cost + margin. */}
                                <span className="text-[10px] text-slate-400 uppercase tracking-wide">or flat</span>
                                <span className="text-sm text-slate-400">$</span>
                                <input
                                  type="number" min="0" step="0.01"
                                  value={svcRate ?? ''}
                                  onChange={e => handleServiceFlatRate(key, e.target.value)}
                                  className={`input w-24 text-sm disabled:bg-slate-100 disabled:text-slate-400 ${svcRateSet && !rowDead ? 'border-emerald-300 bg-emerald-50 font-medium' : ''}`}
                                  placeholder="—"
                                  disabled={rowDead}
                                  title={rowDead
                                    ? `Not used — ${carrier} bills $0 on the client's own account`
                                    : 'Flat rate for this service. Blank means cost + margin.'}
                                />
                                {exceptionBillsAtCost && (
                                  <span
                                    className="text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 shrink-0"
                                    title="This service has a flat rate but no margin here, on its carrier, or on Default. An order that waives the flat rate — a per-order exception, or carrier cost above the threshold — bills at cost with no markup."
                                  >
                                    ⚠ cost if waived
                                  </span>
                                )}
                                {svc.count != null ? (
                                  <span className="text-[10px] text-slate-400 tabular-nums">
                                    {svc.count.toLocaleString()} orders
                                  </span>
                                ) : shippedServicesFor(carrier).length > 0 && (
                                  // This key matches no order this client has
                                  // shipped, so its margin can never apply. Silent
                                  // before: a mistyped service code just quietly
                                  // priced nothing at all.
                                  <span
                                    className="text-[10px] text-amber-600 font-medium whitespace-nowrap"
                                    title={`No orders on ${carrier} use the service "${svc.code}". This margin will never apply — remove it and pick the service from the dropdown instead.`}
                                  >
                                    ⚠ matches no orders
                                  </span>
                                )}
                                {isSet && (
                                  <button
                                    type="button"
                                    onClick={() => clearService(key)}
                                    title="Clear — inherit the carrier margin"
                                    className="text-slate-300 hover:text-red-400 ml-auto"
                                  >
                                    <X size={14} />
                                  </button>
                                )}
                              </div>
                            )
                          })}
                          {/* Add a service — chosen from what this client has
                              actually shipped, never typed by hand. */}
                          {addingFor === carrier ? (
                            <div className="flex items-center gap-2 pt-1">
                              <select
                                autoFocus
                                className="input text-sm flex-1 min-w-0"
                                defaultValue=""
                                onChange={e => addService(carrier, e.target.value)}
                              >
                                <option value="" disabled>
                                  {available.length
                                    ? 'Choose a service…'
                                    : 'No services available'}
                                </option>
                                {available.map(s => (
                                  <option key={s.code} value={s.code}>
                                    {s.name ? `${s.code} — ${s.name}` : s.code}
                                    {s.count != null ? ` (${s.count.toLocaleString()} orders)` : ''}
                                  </option>
                                ))}
                              </select>
                              <button
                                type="button"
                                onClick={() => setAddingFor(null)}
                                className="text-xs text-slate-500 hover:text-slate-700"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : available.length > 0 ? (
                            <button
                              type="button"
                              onClick={() => setAddingFor(carrier)}
                              className="text-xs text-blue-600 hover:text-blue-700 pt-1"
                            >
                              + Add service ({available.length} shipped)
                            </button>
                          ) : servicesFailed ? (
                            <p className="text-[11px] text-red-500 pt-1">
                              Couldn't load shipped services — try reopening this modal.
                            </p>
                          ) : (
                            <p className="text-[11px] text-slate-400 pt-1">
                              {shippedServicesFor(carrier).length === 0
                                ? 'No orders on this carrier in the last 12 months.'
                                : 'Every service shipped on this carrier already has a margin.'}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>

            {/* ----------------------------------------------------------
                Outbound shipping, laid out in the order compute_shipping_cost
                actually resolves it:

                  own account -> escapes -> per-service flat -> client-wide
                  flat -> cost + margin

                Screen order IS precedence order. A block can only be beaten
                by one ABOVE it, so "which setting wins" is readable without
                tracing the Python. These used to be one card titled "Flat
                Shipping Rate Override" that held the rate which LOSES, next
                to a return rate that does not affect outbound at all.
                ---------------------------------------------------------- */}

            {/* Client-wide flat rate. Sits BELOW the carrier/service table
                because it loses to it. */}
            <div className="border border-slate-200 rounded-xl p-4 space-y-3 bg-slate-50">
              <div>
                <h4 className="text-sm font-semibold text-slate-700">
                  Client-wide Flat Rate
                  <span className="font-normal text-slate-400"> — fallback only</span>
                </h4>
                <p className="text-xs text-slate-400 mt-0.5">
                  One rate for every outbound order, used only while no service row
                  above carries a flat rate.
                </p>
              </div>
              <div className="sm:w-1/2">
                <label className="label">Flat Outbound Rate ($)</label>
                <input
                  type="number" min="0" step="0.01"
                  value={form.flat_outbound_rate}
                  onChange={e => setForm(p => ({ ...p, flat_outbound_rate: e.target.value }))}
                  className="input disabled:bg-slate-100 disabled:text-slate-400"
                  placeholder="e.g. 9.95"
                  disabled={anyPerServiceRateKey}
                />
                {/* Gated on the map being NON-EMPTY, not on its values being
                    > 0, because that is the backend's test (plain dict
                    truthiness). Gating on a count of positive values would let
                    a map holding a single 0 leave this field looking live
                    while the backend already ignores it. */}
                {anyPerServiceRateKey ? (
                  <p className="text-xs text-amber-700 mt-1">
                    Ignored — {perServiceRateCount || 'some'} service row
                    {perServiceRateCount === 1 ? '' : 's'} above carry a flat rate.
                    Services with no flat rate of their own bill <b>cost + margin</b>,
                    not this amount.
                  </p>
                ) : (
                  <p className="text-xs text-slate-400 mt-1">
                    Fixed charge per outbound order, except own-account carriers (billed $0).
                  </p>
                )}
              </div>
            </div>

            {/* The three escapes. Grouped because they share an EFFECT — they
                abandon a flat rate for cost + margin — not because they share
                a column. They are a plain OR in pricing.py: one firing is
                enough, and a second firing changes nothing. */}
            <div className="border border-slate-200 rounded-xl p-4 space-y-4 bg-slate-50">
              <div>
                <h4 className="text-sm font-semibold text-slate-700">
                  Flat-Rate Escapes
                  <span className="font-normal text-slate-400"> — when a flat rate is abandoned</span>
                </h4>
                <p className="text-xs text-slate-400 mt-0.5">
                  Ways an order that would bill a flat rate bills actual cost + margin
                  instead. None of them change a service that already bills cost + margin.
                </p>
              </div>

              <div className="sm:w-1/2">
                <label className="label">Exception Threshold ($)</label>
                <input
                  type="number" min="0" step="0.01"
                  value={form.shipping_exception_threshold}
                  onChange={e => setForm(p => ({ ...p, shipping_exception_threshold: e.target.value }))}
                  className="input disabled:bg-slate-100 disabled:text-slate-400"
                  placeholder="e.g. 25.00"
                  disabled={!anyFlatOutboundConfigured}
                />
                {/* Inert without a flat rate: the comparison lives INSIDE the
                    `if flat_rate > 0` block, so with no flat rate this field
                    does nothing at all. Its old hint stated the behaviour
                    unconditionally. */}
                {anyFlatOutboundConfigured ? (
                  <p className="text-xs text-slate-400 mt-1">
                    Bill actual cost + margin when carrier cost exceeds this. Compared
                    against the <b>reconciled or overridden</b> courier cost when one
                    exists, not the raw ShipStation cost.
                  </p>
                ) : (
                  <p className="text-xs text-slate-400 mt-1">
                    No flat rate configured — nothing to escape from.
                  </p>
                )}
              </div>

              {/* Kit pricing — Sapyen only (or any client already configured).
                  Billed per KIT rather than per order/item: an order whose item
                  count is an exact multiple of a kit size bills as N kits at the
                  Case Pick fee, which replaces pick + item fees. Anything that
                  matches no size falls back to the normal rate card.
                  It lives under Escapes because setting a prefix silently blanks
                  BOTH flat-rate controls for multi-kit and international orders. */}
              {(/sapyen|vito health/i.test(client?.name || '') || String(form.kit_component_prefixes || '').trim() !== '') && (
                <div className="rounded-lg border border-violet-200 bg-violet-50/40 p-4">
                  <p className="text-sm font-semibold text-violet-900">
                    Kits &amp; international forfeit the flat rate
                  </p>
                  <p className="text-xs text-violet-700 mt-0.5 mb-3">
                    SKU prefixes that mark a line as a <b>component</b> — e.g. <b>SAP_</b>.
                    Every other line is a kit product, so the kit count is the total quantity of
                    non-component lines, charged at the <b>Case Pick fee</b> each. A
                    components-only order (bulk aliquoting) bills no kit fee. Leave blank to disable.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="label">Component SKU prefixes</label>
                      <input
                        type="text"
                        value={form.kit_component_prefixes}
                        onChange={e => setForm(p => ({ ...p, kit_component_prefixes: e.target.value }))}
                        className="input" placeholder="SAP_"
                      />
                      <p className="text-xs text-slate-400 mt-1">
                        SAHMFAKS×10 → 10 kits · S_SAHMFAKS×15 → 15 kits ·
                        kit fee is the Case Pick fee (${Number(form.case_pick_fee || 0).toFixed(2)}) each
                      </p>
                    </div>
                    <div>
                      <label className="label">Aliquoting fee (per kit)</label>
                      <input
                        type="number" step="0.01" min="0"
                        value={form.order_aliquoting_fee}
                        onChange={e => setForm(p => ({ ...p, order_aliquoting_fee: e.target.value }))}
                        className="input" placeholder="0.30"
                      />
                      <p className="text-xs text-slate-400 mt-1">
                        A consumable used once per kit (the aliquoting medium) that the
                        client does not reliably itemise. Billed off the kit count, so it
                        no longer matters whether the part appears as an order line.
                        Set this and remove the part&apos;s own SKU rate, or it bills twice.
                      </p>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="label">Demo-kit SKUs (no aliquoting)</label>
                      <input
                        type="text"
                        value={form.order_aliquoting_exclude_skus}
                        onChange={e => setForm(p => ({ ...p, order_aliquoting_exclude_skus: e.target.value }))}
                        className="input" placeholder="S_SAHMFAKS"
                      />
                      <p className="text-xs text-slate-400 mt-1">
                        Kits that ship WITHOUT the consumable. Comma separated,
                        <b> *</b> wildcards allowed. Components-only orders (bulk packaging,
                        replacement parts) carry no kit line and are skipped automatically.
                      </p>
                    </div>
                    {/* Was a "Single-kit shipping" tile showing a dollar figure
                        mirrored from flat_outbound_rate — a second label for a
                        value already on screen, which read as a separate
                        setting. It also rendered "$0.00" when no flat rate was
                        configured, implying free shipping when the real
                        behaviour is cost + margin. Now states the rule. */}
                    <div>
                      <label className="label">Shipping on kit orders</label>
                      <div className="input bg-slate-100 text-slate-600 h-auto py-2 text-xs leading-relaxed">
                        {anyFlatOutboundConfigured
                          ? 'A single domestic kit pays whatever flat rate its service carries. Multiple kits, and all international, pay actual cost + margin.'
                          : 'Actual cost + margin — no flat rate is configured.'}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* The per-order controls are NOT in this modal — they are in the
                  orders table. Naming them here is the fix for "I can't find
                  where to do this"; they stay per-order because they are. */}
              <div className="text-xs text-slate-500 bg-white border border-slate-200 rounded-lg px-3 py-2 space-y-1">
                <p>
                  <b>Per-order exception</b> — the lightning-bolt in the Shipping column of
                  the orders table below. Forces that one order to cost + margin. It only
                  appears once a flat rate exists, and does nothing on a service that has none.
                </p>
                <p>
                  <b>Per-order courier-cost override</b> — click any Carrier Cost cell. The
                  override is what the threshold above compares against, so it can flip an
                  order between the flat rate and cost + margin.
                </p>
              </div>
            </div>

            {/* Returns are priced on an entirely separate path and are not
                touched by anything above. This field used to sit in the
                outbound card, where it also switched ON an outbound exception
                banner describing behaviour it cannot cause. */}
            <div className="border border-slate-200 rounded-xl p-4 bg-slate-50">
              <div className="mb-3">
                <h4 className="text-sm font-semibold text-slate-700">Returns</h4>
                <p className="text-xs text-slate-400 mt-0.5">
                  Priced separately. Nothing in the outbound sections above applies here.
                </p>
              </div>
              <div className="sm:w-1/2">
                <label className="label">Flat Return Rate ($)</label>
                <input
                  type="number" min="0" step="0.01"
                  value={form.flat_return_rate}
                  onChange={e => setForm(p => ({ ...p, flat_return_rate: e.target.value }))}
                  className="input" placeholder="e.g. 12.00"
                />
                <p className="text-xs text-slate-400 mt-1">Fixed charge per return label</p>
              </div>
            </div>

          </div>

          <div className="px-6 py-4 border-t border-slate-200 flex gap-3 justify-end">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={mutation.isPending}>
              Cancel
            </button>
            <button type="submit" disabled={mutation.isPending} className="btn-primary">
              {mutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Edit2 size={14} />}
              {mutation.isPending ? 'Saving…' : 'Save Pricing'}
            </button>
          </div>
        </form>
        <ToastContainer toasts={toasts} onClose={removeToast} />
      </div>
      </div>
    </div>
  )
}

const currentMonthStart = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
}
const DEFAULT_FILTERS = { from: currentMonthStart(), to: '', carrier: '', state: '', tag: '', casePick: '', search: '' }
const EMPTY_FILTERS   = { from: '', to: '', carrier: '', state: '', tag: '', casePick: '', search: '' }

export default function AdminClientDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { toasts, addToast, removeToast } = useToast()

  const [activeTab, setActiveTab] = useState('orders')
  // Products tab is in private testing: only logins in PRODUCTS_FEATURE_EMAILS.
  const { data: productsAccess } = useQuery({
    queryKey: ['products-admin-access'],
    queryFn: getProductsAdminAccess,
    staleTime: 5 * 60_000,
    retry: false,
  })
  // Honour ?from=YYYY-MM-DD&to=YYYY-MM-DD on initial load — used by the
  // History page's drill-down so the order/exports/etc tabs land filtered to
  // the historical period the user clicked.
  const [filters, setFilters] = useState(() => ({
    ...DEFAULT_FILTERS,
    from:   searchParams.get('from') || DEFAULT_FILTERS.from,
    to:     searchParams.get('to')   || DEFAULT_FILTERS.to,
  }))
  const [sort, setSort]         = useState({ col: 'shipped_date', dir: 'desc' })
  const [page, setPage]         = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [showPricing, setShowPricing] = useState(false)
  const [showEdit, setShowEdit] = useState(false)
  const [editingOrder, setEditingOrder] = useState(null)

  const { data: pricingData, isLoading: pricingLoading, error: pricingError } = useQuery({
    queryKey: ['admin', 'pricing', id],
    queryFn: () => getClientPricing(id),
    enabled: !!id,
  })

  // Union of both margin maps — a carrier or service priced only with a flat $
  // margin has no carrier_margins key at all, so listing one map alone drops it
  // from the summary. Same rule the editor and the backend use.
  const summaryMarginKeys = Array.from(new Set([
    ...Object.keys(pricingData?.carrier_margins      || {}),
    ...Object.keys(pricingData?.carrier_flat_margins || {}),
  ]))

  // Per-service flat rates. When any are set they supersede the client-wide
  // flat_outbound_rate, and every service they DON'T name bills cost + margin.
  const summaryFlatRates = Object.entries(pricingData?.flat_outbound_rates || {})
    .filter(([, v]) => Number(v) > 0)
  // A flat rate can be waived per order, by the flat_rate_exception flag, under
  // either shape — so the order-table toggle has to follow both.
  const hasAnyFlatOutbound =
    pricingData?.flat_outbound_rate > 0 || summaryFlatRates.length > 0

  const { data: mtdCounts } = useQuery({
    queryKey: ['admin', 'mtd-counts', id],
    queryFn: () => getMtdCounts(id),
    enabled: !!id,
  })

  const [exporting, setExporting] = useState(false)
  const [workbookLoading, setWorkbookLoading] = useState(false)

  const queryClient = useQueryClient()

  const insertTypeMutation = useMutation({
    mutationFn: ({ orderId, insertType }) => updateOrderInsertType(orderId, insertType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'orders', id] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'clients'] })
    },
    onError: () => {
      addToast({ type: 'error', message: 'Failed to update insert type.' })
    },
  })

  function handleInsertTypeChange(orderId, insertType) {
    insertTypeMutation.mutate({ orderId, insertType })
  }

  const casePickMutation = useMutation({
    mutationFn: ({ orderId, qty, splitItems = 0 }) => updateOrderCasePick([orderId], qty > 0, qty, splitItems),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'orders', id] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'clients'] })
      addToast({ type: 'success', message: 'Case pick updated.' })
    },
    onError: (err) => {
      addToast({ type: 'error', message: err.response?.data?.detail || 'Failed to update case pick.' })
    },
  })

  function handleCasePickChange(orderId, qty, splitItems = 0) {
    casePickMutation.mutate({ orderId, qty, splitItems })
  }

  const flatRateExceptionMutation = useMutation({
    mutationFn: ({ orderId, current }) => updateOrderFlatRateException([orderId], !current),
    onSuccess: (_, { current }) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'orders', id] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'clients'] })
      addToast({
        type: 'success',
        message: !current
          ? 'Shipping exception set — order will be charged full carrier rate.'
          : 'Flat rate restored for this order.',
      })
    },
    onError: (err) => {
      addToast({ type: 'error', message: err.response?.data?.detail || 'Failed to update shipping exception.' })
    },
  })

  function handleFlatRateExceptionToggle(orderId, current) {
    flatRateExceptionMutation.mutate({ orderId, current })
  }

  const applyInsertDefaultMutation = useMutation({
    mutationFn: () => applyInsertDefaultToAllOrders(id),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'orders', id] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'clients'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'mtd-counts', id] })
      addToast({ type: 'success', message: `Insert default applied to ${result.updated} orders.` })
    },
    onError: () => addToast({ type: 'error', message: 'Failed to apply insert default.' }),
  })

  const waiveMutation = useMutation({
    mutationFn: ({ orderId, waived }) => waiveOrder(orderId, waived),
    onSuccess: (_, { waived }) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'orders', id] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'clients'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'mtd-counts', id] })
      addToast({ type: 'success', message: waived ? 'Order fees waived.' : 'Order fees restored.' })
    },
    onError: () => addToast({ type: 'error', message: 'Failed to update order.' }),
  })

  const { data: clients = [], isLoading: clientsLoading, error: clientsError } = useQuery({
    queryKey: ['admin', 'clients'],
    queryFn: getAdminClients,
  })
  const client = clients.find((c) => String(c.id) === String(id))

  const params = {
    client_id: id,
    limit:     pageSize,
    offset:    (page - 1) * pageSize,
    sort_by:   sort.col,
    sort_dir:  sort.dir,
    ...(filters.from    && { from:    filters.from    }),
    ...(filters.to      && { to:      filters.to      }),
    ...(filters.carrier  && { carrier:   filters.carrier  }),
    ...(filters.state    && { state:     filters.state    }),
    ...(filters.tag      && { tag:       filters.tag      }),
    ...(filters.casePick && { case_pick: filters.casePick }),
    ...(filters.search   && { search:    filters.search   }),
  }

  function handleSortChange(col) {
    setSort(s => ({ col, dir: s.col === col && s.dir === 'asc' ? 'desc' : 'asc' }))
    setPage(1)
  }

  const { data: ordersData, isLoading: ordersLoading, isFetching, error: ordersError } = useQuery({
    queryKey: ['admin', 'orders', id, params],
    queryFn: () => getAdminOrders(params),
    enabled: !!id,
    keepPreviousData: true,
  })

  const { data: carrierOptions = [] } = useQuery({
    queryKey: ['admin', 'carriers', id],
    queryFn: () => getOrderCarriers(id),
    enabled: !!id,
  })

  const { data: stateOptions = [] } = useQuery({
    queryKey: ['admin', 'states', id],
    queryFn: () => getOrderStates(id),
    enabled: !!id,
  })

  const { data: tagOptions = [] } = useQuery({
    queryKey: ['admin', 'tags', id],
    queryFn: () => getOrderTags(id),
    enabled: !!id,
  })

  const orders = ordersData?.orders || ordersData || []
  const total  = ordersData?.total  || orders.length

  // NOTE: both handlers used to call setSelectedIds(new Set()), but this
  // component has no selection state — the calls were vestigial and threw
  // ReferenceError, blanking the page on any filter or page-size change.
  function handleFilterChange(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }))
    setPage(1)
  }

  function handlePageSizeChange(size) {
    setPageSize(size)
    setPage(1)
  }

  async function handleExport() {
    setExporting(true)
    try {
      await exportOrders({ ...params, limit: undefined, offset: undefined })
    } catch {
      addToast({ type: 'error', message: 'Export failed.' })
    } finally {
      setExporting(false)
    }
  }

  async function handleWorkbookDownload() {
    const now = new Date()
    setWorkbookLoading(true)
    try {
      await downloadClientWorkbook(id, now.getFullYear(), now.getMonth() + 1)
    } catch {
      addToast({ type: 'error', message: 'Workbook export failed.' })
    } finally {
      setWorkbookLoading(false)
    }
  }

  if (clientsLoading) {
    return (
      <div className="space-y-6">
        {/* Keep the escape hatch visible WHILE loading — this view waits on the
            full client list, so without it a slow load traps you on the page. */}
        <button onClick={() => navigate('/admin/clients')} className="btn-secondary">
          <ArrowLeft size={14} /> Back to clients
        </button>
        <div className="skeleton h-8 w-48 rounded" />
        <div className="grid grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => <div key={i} className="skeleton h-24 rounded-xl" />)}
        </div>
        <div className="skeleton h-64 rounded-xl" />
      </div>
    )
  }

  // A failed client-list fetch leaves `clients` at its [] default, so the
  // !client branch below would report the request failing as "Client not
  // found" — a statement about the client rather than about the fetch.
  if (clientsError) {
    return (
      <div className="card p-10 text-center">
        <AlertTriangle size={32} className="mx-auto text-red-400 mb-2" />
        <p className="text-slate-600 text-sm font-medium">Couldn't load clients</p>
        <p className="text-slate-400 text-xs mt-1">
          This is <b>not</b> a missing client — the request failed.
        </p>
        <p className="text-red-600 text-xs mt-2 font-mono break-all">
          {clientsError?.response?.data?.detail || clientsError?.message || 'Unknown error'}
        </p>
        <button onClick={() => navigate('/admin/clients')} className="btn-secondary mt-4 mx-auto">
          <ArrowLeft size={14} /> Back to clients
        </button>
      </div>
    )
  }

  if (!client) {
    return (
      <div className="card p-10 text-center">
        <AlertCircle size={32} className="mx-auto text-red-300 mb-2" />
        <p className="text-slate-500 text-sm">Client not found</p>
        <button onClick={() => navigate('/admin/clients')} className="btn-secondary mt-4 mx-auto">
          <ArrowLeft size={14} /> Back to clients
        </button>
      </div>
    )
  }

  return (
    <>
      <div className="space-y-5">
        {/* Back + heading */}
        <div className="flex flex-wrap items-start gap-4">
          <button onClick={() => navigate('/admin/clients')} className="btn-secondary">
            <ArrowLeft size={15} /> Back
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-xl font-semibold text-slate-800">{client.name}</h2>
              <button
                onClick={() => setShowEdit(true)}
                className="p-1 rounded text-slate-300 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                title="Edit client"
              >
                <Edit2 size={15} />
              </button>
              {client.overdue && (
                <span className="badge bg-red-100 text-red-700 text-xs">
                  <AlertCircle size={10} className="mr-1" />Overdue
                </span>
              )}
              {/* Contract end date. Storage stops after the week containing it,
                  so this materially changes what the client is billed — it
                  belongs beside the name, not buried in the edit modal. */}
              {client.termination_date && (() => {
                let label = String(client.termination_date).slice(0, 10)
                let ended = false
                try {
                  const d = parseISO(client.termination_date)
                  label = format(d, 'd MMM yyyy')
                  ended = d <= new Date()
                } catch { /* fall back to the raw value */ }
                return (
                  <span
                    className={`badge text-xs ${ended
                      ? 'bg-slate-200 text-slate-700'
                      : 'bg-amber-100 text-amber-700'}`}
                    title={ended
                      ? `Contract ended ${label}. Storage stopped after the week containing that date; orders and warehouse activity still bill as they occur.`
                      : `Contract ends ${label}. Storage will stop after the week containing that date.`}
                  >
                    <CalendarX2 size={10} className="mr-1" />
                    {ended ? 'Terminated' : 'Terminates'} {label}
                  </span>
                )
              })()}
            </div>
            {client.email && <p className="text-sm text-slate-400 mt-0.5">{client.email}</p>}
          </div>
          {/* MTD Workbook export */}
          <button
            onClick={handleWorkbookDownload}
            disabled={workbookLoading}
            className="btn-secondary flex items-center gap-1.5 text-sm"
            title="Export MTD workbook as Excel"
          >
            {workbookLoading
              ? <Loader2 size={14} className="animate-spin" />
              : <FileSpreadsheet size={14} />}
            {workbookLoading ? 'Exporting…' : 'Export Workbook'}
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {/* Card 1 — MTD Revenue breakdown */}
          <StatsCard
            title="MTD Total"
            value={formatCurrency(client.mtd_total)}
            subtitle={
              <span className="flex flex-col gap-0.5 mt-1">
                <span className="flex justify-between text-xs text-slate-400">
                  <span>Order Fulfilment</span>
                  <span>{formatCurrency(client.mtd_orders_fulfilment ?? 0)}</span>
                </span>
                <span className="flex justify-between text-xs text-slate-400">
                  <span>Order Shipping</span>
                  <span>{formatCurrency(client.mtd_orders_shipping ?? 0)}</span>
                </span>
                <span className="flex justify-between text-xs text-slate-400">
                  <span>Order Details</span>
                  <span>{formatCurrency(client.mtd_sku_charges ?? 0)}</span>
                </span>
                <span className="flex justify-between text-xs text-slate-400">
                  <span>Returns</span>
                  <span>{formatCurrency(client.mtd_returns ?? 0)}</span>
                </span>
                <span className="flex justify-between text-xs text-slate-400">
                  <span>Exports</span>
                  <span>{formatCurrency(client.mtd_exports ?? 0)}</span>
                </span>
                <span className="flex justify-between text-xs text-slate-400">
                  <span>Storage</span>
                  <span>{formatCurrency(client.mtd_storage ?? 0)}</span>
                </span>
                <span className="flex justify-between text-xs text-slate-400">
                  <span>Activity</span>
                  <span>{formatCurrency(client.mtd_activity ?? 0)}</span>
                </span>
              </span>
            }
            color="blue"
            icon={<DollarSign size={18} className="text-blue-600" />}
          />

          {/* Card 2 — MTD order quantity breakdown */}
          <div className="card p-4">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">MTD Numbers</p>
            <div className="flex flex-col gap-1.5">
              {[
                { label: 'Orders',         value: mtdCounts?.total_orders   ?? '—' },
                { label: '+Items',         value: mtdCounts?.add_items       ?? '—' },
                { label: 'Inserts',        value: mtdCounts?.std_inserts     ?? '—' },
                { label: 'Custom Inserts', value: mtdCounts?.custom_inserts  ?? '—' },
                { label: 'No Insert',      value: mtdCounts?.no_inserts      ?? '—' },
                { label: 'Case Picks',     value: mtdCounts?.case_picks      ?? '—' },
              ].map(({ label, value }) => (
                <div key={label} className="flex justify-between items-center">
                  <span className="text-xs text-slate-500">{label}</span>
                  <span className="text-sm font-semibold text-slate-800 tabular-nums">{value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Card 3 — Outstanding + Last Sync combined */}
          <div className="card p-4 flex flex-col gap-4">
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Outstanding (all-time)</p>
              <p className={`text-2xl font-bold ${client.outstanding > 0 ? 'text-red-600' : 'text-slate-800'}`}>
                {formatCurrency(client.outstanding)}
              </p>
              <p className="text-xs text-slate-400 mt-0.5">
                {client.outstanding > 0 ? 'Payment pending' : 'Account current'}
              </p>
            </div>
            <div className="border-t border-slate-100 pt-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Last Sync</p>
              <p className="text-lg font-bold text-slate-800">
                {client.last_sync ? format(parseISO(client.last_sync), 'dd MMM HH:mm') : 'Never'}
              </p>
              <p className="text-xs text-slate-400 mt-0.5">
                {client.last_sync ? format(parseISO(client.last_sync), 'yyyy') : 'No sync data'}
              </p>
            </div>
          </div>
        </div>

        {/* Tab navigation */}
        <div className="flex gap-1 border-b border-slate-200 overflow-x-auto">
          {[
            { key: 'orders',       label: 'Orders',        icon: <ClipboardList size={14} /> },
            { key: 'order-details',label: 'Order Details', icon: <Receipt size={14} /> },
            { key: 'self-ship',    label: 'Self Ship',      icon: <PackageCheck size={14} /> },
            { key: 'returns',      label: 'Returns',        icon: <RotateCcw size={14} /> },
            { key: 'exports',      label: 'Exports',        icon: <Globe size={14} /> },
            { key: 'storage',      label: 'Storage',        icon: <Package size={14} /> },
            { key: 'packaging',    label: 'Packaging',      icon: <Box size={14} /> },
            ...(productsAccess?.allowed ? [{ key: 'products', label: 'Products', icon: <Tags size={14} /> }] : []),
            { key: 'warehouse',    label: 'Activity',       icon: <Activity size={14} /> },
            { key: 'discrepancies',label: 'Discrepancies',  icon: <AlertTriangle size={14} /> },
            { key: 'rollover',     label: 'Rollover',       icon: <History size={14} /> },
            // Trudell-only — surfaces a live ShipStation per-shipment breakdown
            ...((client?.name || '').toLowerCase().includes('trudell')
              ? [{ key: 'detail-report', label: 'Detail Report', icon: <Stethoscope size={14} /> }]
              : []),
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap shrink-0 ${
                activeTab === tab.key
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* Order Details tab */}
        {activeTab === 'order-details' && <OrderDetailsTab clientId={id} insertTriggerSku={pricingData?.insert_trigger_sku} />}

        {/* Self Ship tab */}
        {activeTab === 'self-ship' && <SelfShipTab clientId={id} />}

        {/* Returns tab */}
        {activeTab === 'returns' && <ReturnsTab clientId={id} />}

        {/* Exports tab */}
        {activeTab === 'exports' && <ExportsTab clientId={id} />}

        {/* Storage tab */}
        {activeTab === 'storage' && <StorageTab clientId={id} />}

        {activeTab === 'packaging' && <PackagingTab clientId={id} clientName={client?.name} />}

        {activeTab === 'products' && productsAccess?.allowed && <ProductsTab key={id} clientId={id} clientName={client?.name} mode="admin" />}

        {/* Warehouse tab */}
        {activeTab === 'warehouse' && (
          <WarehouseTab clientId={id} hasSlackChannel={!!client?.slack_channel_id} />
        )}

        {/* Postage Discrepancies tab */}
        {activeTab === 'discrepancies' && <PostageDiscrepanciesTab clientId={id} />}

        {/* Rollover — prior-period charges queued for the next invoice (not in MTD) */}
        {activeTab === 'rollover' && <RolloverTab clientId={id} />}

        {activeTab === 'detail-report' && <DetailReportTab clientId={id} />}

        {/* Orders section */}
        {activeTab === 'orders' && <div>
          {/* Pricing */}
          <div className="card p-4 mb-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-700">Pricing</h3>
              <button onClick={() => setShowPricing(true)} className="btn-secondary text-xs py-1 px-3">
                <Edit2 size={12} /> Edit Pricing
              </button>
            </div>
            {pricingLoading ? (
              <div className="skeleton h-8 rounded" />
            ) : pricingError ? (
              /* Without this branch a failed fetch falls through to the `null`
                 below and the card renders blank — reading as "no pricing
                 configured" when the rate card is merely unknown. */
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start gap-2">
                <AlertTriangle size={16} className="text-red-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-800">Couldn't load pricing</p>
                  <p className="text-xs text-red-700 mt-0.5">
                    This is <b>not</b> an empty rate card. Don't open Edit Pricing until it
                    loads — the form would open on defaults and saving would overwrite the
                    client's real rates.
                  </p>
                  <p className="text-xs text-red-600 font-mono mt-1 break-all">
                    {pricingError?.response?.data?.detail || pricingError?.message || 'Unknown error'}
                  </p>
                </div>
              </div>
            ) : pricingData ? (
              <div className="space-y-3 text-sm">
                {pricingData.pick_fee_tiers?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Pick Fee Tiers</p>
                    <div className="flex flex-wrap gap-2">
                      {pricingData.pick_fee_tiers.map((t, i) => (
                        <span key={i} className="badge bg-blue-50 text-blue-700 text-xs">
                          {t.min_orders}–{t.max_orders ?? '∞'} orders: ${Number(t.fee).toFixed(2)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {pricingData.additional_item_fee_tiers?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Additional Item Fee Tiers</p>
                    <div className="flex flex-wrap gap-2">
                      {pricingData.additional_item_fee_tiers.map((t, i) => (
                        <span key={i} className="badge bg-purple-50 text-purple-700 text-xs">
                          {t.min_orders}–{t.max_orders ?? '∞'} orders: ${Number(t.fee).toFixed(2)}/item
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex flex-wrap gap-5">
                  {pricingData.labelling_fee != null && (
                    <div><span className="text-slate-500">Labelling:</span>{' '}<span className="font-medium">{formatCurrency(pricingData.labelling_fee)}</span></div>
                  )}
                  {pricingData.insert_fee != null && (
                    <div><span className="text-slate-500">Insert:</span>{' '}<span className="font-medium">{formatCurrency(pricingData.insert_fee)}</span></div>
                  )}
                  {pricingData.custom_insert_fee != null && (
                    <div><span className="text-slate-500">Custom Insert:</span>{' '}<span className="font-medium">{formatCurrency(pricingData.custom_insert_fee)}</span></div>
                  )}
                  {pricingData.case_pick_fee != null && (
                    <div><span className="text-slate-500">Case Pick:</span>{' '}<span className="font-medium">{formatCurrency(pricingData.case_pick_fee)}/case</span></div>
                  )}
                </div>
                {pricingData.default_no_insert && (
                  <div className="flex items-center gap-2 mt-1">
                    <span className="inline-flex items-center text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 rounded px-2 py-0.5">
                      No Insert by Default
                    </span>
                    <span className="text-xs text-slate-400">— new orders sync with $0 insert fee</span>
                  </div>
                )}
                {parseTriggerSkus(pricingData.insert_trigger_sku).length > 0 && (
                  <div className="flex items-start gap-2 mt-1 flex-wrap">
                    <span className="text-slate-500 text-xs whitespace-nowrap">
                      Insert Trigger {parseTriggerSkus(pricingData.insert_trigger_sku).length === 1 ? 'SKU' : 'SKUs'}:
                    </span>
                    <div className="flex flex-wrap gap-1">
                      {parseTriggerSkus(pricingData.insert_trigger_sku).map(sku => (
                        <span key={sku} className="font-mono text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 rounded px-2 py-0.5">
                          {sku}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {(hasAnyFlatOutbound || pricingData.flat_return_rate > 0) && (
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Flat Shipping Rates</p>
                    <div className="flex flex-wrap gap-2">
                      {/* Per-service rates replace the client-wide one, so show
                          one or the other, never both. */}
                      {summaryFlatRates.length > 0 ? (
                        summaryFlatRates.map(([key, rate]) => (
                          <span key={key} className="badge bg-indigo-50 text-indigo-700 text-xs">
                            {key.replace(':', ' \u203a ')}: {formatCurrency(rate)}/order
                          </span>
                        ))
                      ) : pricingData.flat_outbound_rate > 0 && (
                        <span className="badge bg-indigo-50 text-indigo-700 text-xs">
                          Outbound: {formatCurrency(pricingData.flat_outbound_rate)}/order
                        </span>
                      )}
                      {summaryFlatRates.length > 0 && (
                        <span className="badge bg-slate-50 text-slate-600 text-xs">
                          All other services: cost + margin
                        </span>
                      )}
                      {pricingData.flat_return_rate > 0 && (
                        <span className="badge bg-indigo-50 text-indigo-700 text-xs">
                          Returns: {formatCurrency(pricingData.flat_return_rate)}/label
                        </span>
                      )}
                      {pricingData.shipping_exception_threshold > 0 && (
                        <span className="badge bg-amber-50 text-amber-700 text-xs">
                          Exception &gt; {formatCurrency(pricingData.shipping_exception_threshold)}
                        </span>
                      )}
                    </div>
                  </div>
                )}
                {/* Rendered independently of the flat rates above. These used to
                    be branches of one ternary, so a flat RETURN rate hid the
                    OUTBOUND margins entirely — unrelated settings, and it made a
                    client with no carrier margin at all indistinguishable from
                    one whose margins were simply not displayed. */}
                {summaryMarginKeys.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                      Shipping Margins
                      {summaryFlatRates.length > 0 ? (
                        <span className="normal-case font-normal text-slate-400 tracking-normal">
                          {' '}— applies to every service without a flat rate
                        </span>
                      ) : pricingData.flat_outbound_rate > 0 && (
                        <span className="normal-case font-normal text-slate-400 tracking-normal">
                          {' '}— exceptions only, flat outbound rate applies
                        </span>
                      )}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {summaryMarginKeys.map(carrier => {
                        const margin = pricingData.carrier_margins?.[carrier]
                        const flat   = pricingData.carrier_flat_margins?.[carrier]
                        // "australia_post:express" reads as "australia_post › express"
                        const label = carrier === 'default'
                          ? 'Default'
                          : carrier.replace(':', ' › ')
                        // Same union rule as the editor: a flat-only key has no
                        // percent at all, so don't render a bare "undefined%".
                        const parts = []
                        if (margin) parts.push(`${margin}%`)
                        if (flat) parts.push(`+$${Number(flat).toFixed(2)}`)
                        if (!parts.length) return null
                        return (
                          <span key={carrier} className="badge bg-slate-100 text-slate-600 text-xs">
                            {label}: {parts.join(' ')}
                          </span>
                        )
                      })}
                    </div>
                  </div>
                )}
                {pricingData.tag_fees?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Tag Fees</p>
                    <div className="flex flex-wrap gap-2">
                      {pricingData.tag_fees.map((tf, i) => (
                        <span key={i} className="badge bg-teal-50 text-teal-700 text-xs">
                          {tf.tag}: {formatCurrency(tf.fee)}/order
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>

          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-800">Orders</h3>
            {pricingData?.insert_fee > 0 && (
              <button
                onClick={() => applyInsertDefaultMutation.mutate()}
                disabled={applyInsertDefaultMutation.isPending}
                className="btn-secondary text-xs py-1.5 px-3 flex items-center gap-1.5"
                title={pricingData?.default_no_insert
                  ? 'Set all orders to No Insert (current default)'
                  : 'Set all orders to Standard Insert (current default)'}
              >
                {applyInsertDefaultMutation.isPending && <Loader2 size={12} className="animate-spin" />}
                Apply Insert Default to All Orders
              </button>
            )}
          </div>
          <FilterBar
            filters={filters}
            onFilterChange={handleFilterChange}
            onClear={() => { setFilters(DEFAULT_FILTERS); setPage(1) }}
            onExport={handleExport}
            exporting={exporting}
            carriers={carrierOptions}
            states={stateOptions}
            tags={tagOptions}
            showCasePick
          />

          <OrderTable
            orders={orders}
            loading={ordersLoading || isFetching}
            total={total}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={handlePageSizeChange}
            error={ordersError}
            showCarrierCost={true}
            defaultNoInsert={!!pricingData?.default_no_insert}
            sort={sort}
            onSortChange={handleSortChange}
            onInsertTypeChange={handleInsertTypeChange}
            onCasePickChange={handleCasePickChange}
            onFlatRateExceptionToggle={hasAnyFlatOutbound ? handleFlatRateExceptionToggle : undefined}
            onWaive={(orderId, currentWaived) => waiveMutation.mutate({ orderId, waived: !currentWaived })}
            onEditOrder={setEditingOrder}
            getTrackingEvents={getOrderTrackingEventsFull}
          />

        </div>}
      </div>

      {editingOrder && (
        <EditOrderFeesModal
          row={editingOrder}
          onClose={() => setEditingOrder(null)}
          invalidateKeys={[['admin', 'orders', id]]}
        />
      )}

      {showPricing && !pricingLoading && (
        <PricingModal
          client={{ ...client, pricing: pricingData }}
          carriers={carrierOptions}
          onClose={() => setShowPricing(false)}
          onSuccess={() => {
            setShowPricing(false)
            addToast({ type: 'success', message: 'Pricing updated successfully.' })
          }}
        />
      )}

      {showEdit && client && (
        <EditClientModal
          client={client}
          onClose={() => setShowEdit(false)}
          onSuccess={() => {
            setShowEdit(false)
            queryClient.invalidateQueries({ queryKey: ['admin', 'clients'] })
            addToast({ type: 'success', message: 'Client updated.' })
          }}
        />
      )}

      <ToastContainer toasts={toasts} onClose={removeToast} />
    </>
  )
}
